// ── Athena Focus Mission — detection, tracking, persistence ─────────────────

const MISSION_TYPES = [
  {
    type: 'bible_study',
    re: /salmo\s*\d+|salmos?\s+\d+|b[íi]blia|vers[íi]culo|passagem\s+b[íi]blic|estudo\s+b[íi]blic|evangelho\s+de|apocalipse|g[êe]nesis|mateus|marcos|lucas|jo[aã]o\s*\d|romanos|cor[íi]nt|filipenses|efesios|hebreus|tiago/i,
    steps: ['Contexto histórico', 'Leitura do texto', 'Explicação versículo a versículo', 'Aplicação prática', 'Reflexão final'],
    label: 'Estudo',
  },
  {
    type: 'project',
    re: /projeto\s+\w+|vamos\s+(?:terminar|concluir|finalizar)\s+(?:o\s+|a\s+)?\w+|continuar\s+(?:o\s+|a\s+)?\w+\s+projeto|athena|outframe/i,
    steps: ['Definir objetivos', 'Planejar etapas', 'Executar', 'Revisar', 'Concluir'],
    label: 'Projeto',
  },
  {
    type: 'planning',
    re: /quero\s+organizar|planejar\s+(?:meu|o|a|os|as)\s+|me\s+ajuda\s+a\s+planejar|organizar\s+(?:meu|o|a)\s+|meu\s+dia|minhas\s+tarefas|agenda\s+de/i,
    steps: ['Mapear o que há', 'Priorizar', 'Definir ações', 'Organizar blocos', 'Revisar plano'],
    label: 'Planejamento',
  },
  {
    type: 'content',
    re: /criar\s+(?:um|uma)\s+(?:post|texto|roteiro|copy|campanha|email|proposta|apresenta|conte[uú]do|artigo)|escrever\s+(?:um|uma)\s+\w+|redigir\s+|elaborar\s+(?:um|uma)/i,
    steps: ['Briefing', 'Rascunho', 'Revisão de conteúdo', 'Ajuste de tom', 'Versão final'],
    label: 'Conteúdo',
  },
  {
    type: 'study',
    re: /quero\s+(?:estudar|aprender|entender|explorar)\s+\w+|estudar\s+sobre|aprender\s+(?:sobre|como|a)\s+|entender\s+(?:melhor\s+)?\w+/i,
    steps: ['Introdução', 'Conceitos principais', 'Exemplos práticos', 'Prática', 'Revisão'],
    label: 'Estudo',
  },
  {
    type: 'work',
    re: /vamos\s+(?:trabalhar|resolver|analisar|revisar|melhorar|fazer)\s+|me\s+ajuda\s+(?:a\s+)?(?:resolver|analisar|melhorar|criar|desenvolver|montar)|quero\s+(?:resolver|analisar|criar|montar)/i,
    steps: ['Entender o problema', 'Analisar opções', 'Definir solução', 'Executar', 'Validar'],
    label: 'Tarefa',
  },
];

const INTENT_RE = /(?:quero|vamos|me\s+ajuda(?:\s+a)?)\s+(?:estudar|aprender|entender|organizar|resolver|criar|fazer|terminar|continuar|planejar|trabalhar|construir|desenvolver|analisar|montar|redigir|explorar)/i;

const WIN_POOL = [
  'Boa. Etapa concluída.',
  'Ótimo avanço.',
  'Isso ficou claro.',
  'Progresso real.',
  'Você está avançando bem.',
  'Missão quase completa.',
  'Cada etapa conta.',
];

export function detectFocusMission(message) {
  const msg = (message || '').trim();
  if (msg.length < 12) return null;
  if (!INTENT_RE.test(msg)) return null;

  for (const { type, re, steps, label } of MISSION_TYPES) {
    if (re.test(msg)) return _buildMission(msg, type, steps, label);
  }

  if (msg.length > 20) {
    return _buildMission(msg, 'work', ['Entender o objetivo', 'Planejar abordagem', 'Executar', 'Revisar', 'Concluir'], 'Missão');
  }
  return null;
}

function _buildMission(message, type, steps, label) {
  return {
    id: Date.now().toString(36),
    title: _extractTitle(message, type, label),
    type,
    steps,
    progress: 0,
    currentStep: 0,
    wins: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    dismissed: 0,
  };
}

function _extractTitle(msg, type, label) {
  const bibleM = msg.match(/(?:salmo\s*\d+|apocalipse|g[êe]nesis|mateus|lucas|jo[aã]o\s*\d+|romanos|cor[íi]nt\w*)/i);
  if (bibleM) return `Estudo: ${bibleM[0].trim()}`;

  const subjM = msg.match(/(?:quero\s+(?:estudar|aprender|entender|organizar)|vamos\s+\w+)\s+([^.!?\n]{3,30})/i);
  if (subjM) {
    const subj = subjM[1].trim().replace(/^(o|a|os|as|um|uma)\s+/i, '');
    return `${label}: ${subj.slice(0, 35)}`;
  }

  const clean = msg.replace(/^(quero|vamos|me\s+ajuda\s+a?)\s+/i, '').slice(0, 40).trim();
  return `${label}: ${clean}`;
}

export function advanceMission(mission) {
  if (!mission) return mission;
  const next = Math.min(mission.currentStep + 1, mission.steps.length - 1);
  const progress = Math.round((next / Math.max(mission.steps.length - 1, 1)) * 100);
  const win = WIN_POOL[Math.min(mission.wins.length, WIN_POOL.length - 1)];
  return {
    ...mission,
    currentStep: next,
    progress,
    wins: [...mission.wins, { text: win, at: new Date().toISOString() }],
    updatedAt: new Date().toISOString(),
  };
}

// Parse [FOCUS_STEP:done] from backend response — returns { stepDone, cleanText }
export function parseFocusUpdate(text) {
  if (!text) return { stepDone: false, cleanText: text };
  if (!/\[FOCUS_STEP:done\]/i.test(text)) return { stepDone: false, cleanText: text };
  const cleanText = text.replace(/\s*\[FOCUS_STEP:done\]\s*/gi, ' ').trim();
  return { stepDone: true, cleanText };
}

// Persistence
export function saveMission(mission) {
  try {
    if (!mission) { localStorage.removeItem('athena_focus_mission'); return; }
    localStorage.setItem('athena_focus_mission', JSON.stringify(mission));
  } catch {}
}

export function loadMission() {
  try {
    const raw = localStorage.getItem('athena_focus_mission');
    if (!raw) return null;
    const m = JSON.parse(raw);
    // Expire missions older than 24h
    if (m.createdAt && Date.now() - new Date(m.createdAt).getTime() > 86_400_000) {
      localStorage.removeItem('athena_focus_mission');
      return null;
    }
    return m;
  } catch { return null; }
}
